import { cn } from "@faire/prototype-playground/lib/utils";

import { ADS_MANAGER_SURFACE_CLASS } from "../constants/layout";
import { getP8BrandName } from "../p8AdvertiserData";

const FAIRE_LOGO_SRC = "https://cdn.faire.com/static/logo.svg";

function getBrandInitials(name: string): string {
  return name
    .split(/\s+/)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function ChevronNavIcon({
  rotation = 0,
  className,
}: {
  rotation?: number;
  className?: string;
}) {
  return (
    <svg
      className={className}
      width={8}
      height={8}
      viewBox="0 0 8 8"
      fill="none"
      style={{ transform: `rotate(${rotation}deg)` }}
      aria-hidden
    >
      <path
        d="M2 1.5L6 4L2 6.5"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function BellIcon() {
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M9.021 20.045a2.71 2.71 0 0 0 2.622 2.028 2.71 2.71 0 0 0 2.621-2.028M12 1.927c1.864 0 3.652.769 4.97 2.136A7.43 7.43 0 0 1 19.03 9.22c0 8.11 2.85 8.327 3.621 8.327H1.35c.786 0 3.621-.235 3.621-8.327 0-1.934.74-3.79 2.059-5.157A6.9 6.9 0 0 1 12 1.927Z"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function BrandPortalSidebar() {
  const brandName = getP8BrandName();
  const brandInitials = getBrandInitials(brandName);
  // Hover and selected states are translucent ink rather than a near-white
  // tint, so they still register against the off-white nav surface.
  const parentRow =
    "flex items-center justify-between px-6 py-2 text-sm text-[#333] hover:bg-[#3333330f]";
  const child =
    "block py-2 pl-10 pr-4 text-sm text-[#333] hover:bg-[#3333330f]";
  const selected = "bg-[#33333314]";

  return (
    <nav
      className={`flex h-full min-h-0 w-[256px] shrink-0 flex-col overflow-hidden border-r border-[#dfe0e1] ${ADS_MANAGER_SURFACE_CLASS}`}
      aria-label="Main"
    >
      <div className="flex shrink-0 items-center justify-between px-6 py-8">
        <span className="shrink-0">
          <img
            src={FAIRE_LOGO_SRC}
            width={87}
            height={24}
            alt="Faire Logo"
            className="h-6 w-[87px]"
          />
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            aria-label="Updates"
            className="flex h-9 w-9 items-center justify-center rounded text-[#333] hover:bg-[#f7f7f7]"
          >
            <BellIcon />
          </button>
          <button
            type="button"
            aria-label="Account menu"
            className="flex h-10 w-10 items-center justify-center rounded-full bg-[#E8D5C4] text-sm font-medium text-[#333]"
          >
            {brandInitials}
          </button>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-0 overflow-y-auto overscroll-contain pb-6 text-[14px] leading-5">
        <span className={parentRow}>Home</span>
        <div>
          <div className={parentRow}>
            <span>Orders</span>
            <ChevronNavIcon className="text-[#333]" />
          </div>
        </div>
        <span className={cn(parentRow, "gap-2")}>
          <span>Messages</span>
          <span className="text-fs-text-primary-inverse flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[#333] px-1.5 text-xs font-medium">
            1
          </span>
        </span>
        <div>
          <div className={parentRow}>
            <span>Products</span>
            <ChevronNavIcon className="text-[#333]" />
          </div>
        </div>
        <div>
          <div className={parentRow}>
            <span>Customers</span>
            <ChevronNavIcon className="text-[#333]" />
          </div>
        </div>
        <div>
          <div className={cn(parentRow, selected, "font-medium")}>
            <span>Marketing</span>
            <ChevronNavIcon rotation={270} className="text-[#333]" />
          </div>
          {["Campaigns", "Automations", "Promotions"].map((label) => (
            <span key={label} className={child}>
              {label}
            </span>
          ))}
          <span
            className={cn(
              selected,
              "block py-2 pr-4 pl-10 text-sm font-medium text-[#333]"
            )}
            aria-current="page"
          >
            Promoted Listings
          </span>
          {["Insider Partnership", "Events"].map((label) => (
            <span key={label} className={child}>
              {label}
            </span>
          ))}
        </div>
        <div>
          <div className={parentRow}>
            <span>Analytics</span>
            <ChevronNavIcon className="text-[#333]" />
          </div>
        </div>
        <div>
          <div className={parentRow}>
            <span>My shop</span>
            <ChevronNavIcon className="text-[#333]" />
          </div>
        </div>
        <div>
          <div className={parentRow}>
            <span>Settings</span>
            <ChevronNavIcon className="text-[#333]" />
          </div>
        </div>
      </div>
    </nav>
  );
}
